import React, { useEffect, useState } from 'react';
import {
  Row,
  Col,
  Button,
  FormGroup,
  Label,
  Modal,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Alert,
  Input
} from 'reactstrap';
import { AvForm, AvField } from 'availity-reactstrap-validation';
import { connect } from 'react-redux';
import SweetAlert from 'react-bootstrap-sweetalert';
import Select from 'react-select';
import { addNewAttendance, getLeaveBalance } from '../../api/attendance';
import { attendanceType } from '../Utility/Enumerator';

const CreateAttendanceModal = ({
  modalOpenState,
  closeModalHandler,
  fetchNewAttendanceList,
  authUser,
  setLeaveBalance,
  leaveBalance
}) => {
  const [typeOptions, setTypeOptions] = useState([]);
  const [selectedType, setSelectedType] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [requestError, setRequestError] = useState({
    show_alert: false,
    message: ''
  });
  const [attendanceCreateRequestLoading, setAttendanceCreateRequestLoading] =
    useState(false);
  const [successAlert, setSuccessAlert] = useState({
    message: '',
    show_alert: false
  });
  const [paidTotal, setPaidTotal] = useState(0);
  const [paidAvailable, setPaidAvailable] = useState(0);
  const [leaveDays, setLeaveDays] = useState(0);

  async function fetchLeaveBalance() {
    const response = await getLeaveBalance();

    if (!response) return;

    setPaidTotal(response.paid_total);
    setPaidAvailable(response.paid_available);
  }

  function calculateLeaveDays(start, end) {
    const s = new Date(start);
    const e = new Date(end);
    return Math.floor((e - s) / (1000 * 60 * 60 * 24)) + 1;
  }

  async function handleAttendanceCreation(event, values) {
  resetErrorAlert();

  if (!authUser?._id) {
    showErrorAlert('User not authenticated');
    return;
  }

  if (!selectedType) {
    showErrorAlert('Leave type is required');
    return;
  }

  if (new Date(values.start_date) > new Date(values.end_date)) {
    showErrorAlert('Start date cannot be greater than end date');
    return;
  }

  try {
    setAttendanceCreateRequestLoading(true);

    const leaveDays = calculateLeaveDays(
      values.start_date,
      values.end_date
    );

    const { response, error } = await addNewAttendance(
      values.reason,
      values.start_date,
      values.end_date,
      selectedType.value
    );

    if (error) {
      showErrorAlert(error);
      return;
    }

    // 🔥 Optimistic leaveBalance update
    setLeaveBalance((prev) => {
      const updated = { ...prev };

      if (selectedType.value === 'Paid Leave') {
        updated.paid = {
          ...prev.paid,
          requested: prev.paid.requested + leaveDays,
          available: prev.paid.available - leaveDays
        };
      }

      if (selectedType.value === 'Saturday Leave') {
        updated.saturday = {
          ...prev.saturday,
          requested: prev.saturday.requested + 1,
          available: prev.saturday.available - 1
        };
      }

      if (selectedType.value === 'Unpaid Leave') {
        updated.unpaid = {
          ...prev.unpaid,
          requested: prev.unpaid.requested + leaveDays
        };
      }

      return updated;
    });

    setSelectedType(null);
    setStartDate('');
    setEndDate('');
    closeModalHandler();
    showSuccessAlert(response.message);

    await fetchNewAttendanceList();
  } catch (err) {
    showErrorAlert('Something went wrong. Please try again.');
  } finally {
    setAttendanceCreateRequestLoading(false);
  }
}

  function showErrorAlert(message) {
    setRequestError({ message, show_alert: true });
  }

  function resetErrorAlert() {
    setRequestError({ message: '', show_alert: false });
  }

  function showSuccessAlert(message) {
    setSuccessAlert({ message, show_alert: true });
  }

  function resetSuccessAlert() {
    setSuccessAlert({ message: '', show_alert: false });
  }

  function resetState() {
    resetSuccessAlert();
    resetErrorAlert();
  }

  const handleTypeSelection = (selectedItem) => {
    setSelectedType(selectedItem);
    setStartDate('');
    setEndDate('');
  };

  function buildListOptions(list) {
    return list.map((item) => ({
      value: item,
      label: item
    }));
  }

  useEffect(() => {
    if (authUser && modalOpenState) {
      setTypeOptions(buildListOptions(attendanceType));

      fetchLeaveBalance();
    }
  }, [authUser, modalOpenState]);


  const paidTotalLeaves = authUser?.leaves?.paid_limit_per_year ?? 0;
  const paidRequestedLeaves = leaveBalance?.paid?.requested ?? 0;
  const paidConsumedLeaves = leaveBalance?.paid?.consumed ?? 0;

  const availablePaid =
    leaveBalance?.paid?.available ??
    paidTotalLeaves - paidRequestedLeaves - paidConsumedLeaves;

  const isCasualDisabled =
    selectedType?.value === 'Paid Leave' && paidAvailable <= 0;

    useEffect(() => {
      if (startDate && endDate) {
        if (new Date(startDate) <= new Date(endDate)) {
          setLeaveDays(calculateLeaveDays(startDate, endDate));
        } else {
          setLeaveDays(0);
        }
      } else {
        setLeaveDays(0);
      }
    }, [startDate, endDate]);

  return (
    <>
      {successAlert.show_alert && (
        <SweetAlert success title="Created" onConfirm={resetState}>
          {successAlert.message}
        </SweetAlert>
      )}

      <Modal isOpen={modalOpenState} backdrop="static" size="xl">
        <ModalHeader toggle={closeModalHandler}>
          Add Attendance Request
        </ModalHeader>

        <ModalBody>
          {requestError.show_alert && (
            <Alert
              color="danger"
              role="alert"
              isOpen={requestError.show_alert}
              toggle={resetErrorAlert}
            >
              <i className="mdi mdi-block-helper mr-2"></i>
              {requestError.message}
            </Alert>
          )}

          <AvForm
            className="needs-validation"
            onValidSubmit={handleAttendanceCreation}
          >
            <Row>
              <Col md="12">
                <FormGroup>
                  <Label>Reason</Label>
                  <AvField
                    name="reason"
                    placeholder="Enter Reason ..."
                    type="text"
                    className="form-control"
                    validate={{
                      required: {
                        value: true,
                        errorMessage: 'Reason is required'
                      }
                    }}
                  />
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label>Type</Label>
                  <Select
                    options={typeOptions}
                    onChange={handleTypeSelection}
                    value={selectedType}
                    isClearable
                  />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label>Paid Leave Left</Label>
                  <AvField
                    name="leave_left"
                    type="text"
                    className="form-control"
                    value={`${availablePaid} / ${paidTotalLeaves}`}
                    readOnly
                  />
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label>Start Date</Label>
                  <AvField
                    name="start_date"
                    type="date"
                    className="form-control"
                    value={startDate}
                    onChange={(e) => {
                      const v = e.target.value;
                      if (selectedType?.value === 'Saturday Leave') {
                        if (new Date(v).getDay() !== 6) {
                          showErrorAlert('Please select a Saturday');
                          return;
                        }
                        resetErrorAlert();
                        setStartDate(v);
                        setEndDate(v);
                        return;
                      }
                      setStartDate(v);
                    }}
                    validate={{
                      required: {
                        value: true,
                        errorMessage: 'Start date is required'
                      }
                    }}
                  />
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label>End Date</Label>
                  <AvField
                    name="end_date"
                    type="date"
                    className="form-control"
                    value={endDate}
                    disabled={selectedType?.value === 'Saturday Leave'}
                    onChange={(e) => setEndDate(e.target.value)}
                    validate={{
                      required: {
                        value: true,
                        errorMessage: 'End date is required'
                      }
                    }}
                  />
                </FormGroup>
              </Col>

              <Col md="6">
                  <FormGroup>
                    <Label>Days</Label>
                    <Input
                      name="days"
                      type="text"
                      className="form-control"
                      value={leaveDays || 0}
                      readOnly
                    />
                  </FormGroup>
              </Col>
            </Row>

            <ModalFooter>
              <Button
                disabled={attendanceCreateRequestLoading}
                type="button"
                color="light"
                onClick={closeModalHandler}
              >
                Close
              </Button>
              <Button
                disabled={attendanceCreateRequestLoading || isCasualDisabled}
                type="submit"
                color="success"
              >
                {attendanceCreateRequestLoading
                  ? 'Creating ...'
                  : 'Add Request'}
              </Button>
            </ModalFooter>
          </AvForm>
        </ModalBody>
      </Modal>
    </>
  );
};

const mapStateToProps = (state) => ({
  authUser: state.Login?.user
});

export default connect(mapStateToProps)(CreateAttendanceModal);
